<template>
  <button
    class="btn btn-warning btn-sm"
    data-testid="print-btn"
    @click="handlePrint"
    :disabled="loading">
    <ProgressSpinner v-if="loading" style="width: 1rem; height: 1rem" strokeWidth="4" />
    <i v-else class="pi pi-print"></i> 
    {{ text || 'Imprimir' }}
  </button>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import ProgressSpinner from 'primevue/progressspinner';
import { useDateFormatter } from '../../composables/useDateFormatter';

const props = defineProps<{
  text?: string;
  config: Record<string, any>;
  data?: any[];
  getFilteredData?: () => any[];
}>();

const emit = defineEmits<{
  (e: 'print'): void;
  (e: 'print-error', error: any): void;
}>();

// Get i18n for formatting
const { n } = useI18n();
const { formatDate, formatDateTime } = useDateFormatter();

const loading = ref(false);

const handlePrint = async () => {
  if (loading.value) return;
  
  try {
    loading.value = true;
    
    await printTable();
    
    // Emit success event
    emit('print');
    
  } catch (error) {
    console.error('Error printing table:', error);
    emit('print-error', error);
  } finally {
    loading.value = false;
  }
};

const printTable = async () => {
  try {
    // Get filtered data or all data
    const filtered = props.getFilteredData?.() ?? [];
    const dataToExport = filtered.length > 0 ? filtered : (props.data ?? []);
    
    if (!dataToExport || dataToExport.length === 0) {
      throw new Error('No data to print');
    }
    
    // Get visible columns
    const columns = props.config.columns.filter((col: any) => col.visible !== false);

    // Create print content
    const printContent = generatePrintContent(dataToExport, columns);
    
    // Open print window
    openPrintWindow(printContent);
    
  } catch (error) {
    console.error('Print Error:', error);
    throw new Error(`Failed to print table: ${error.message}`);
  }
};

const generatePrintContent = (data: any, columns: any) => {
  const title = props.config.title || 'Relatório';
  const currentDate = formatDate(new Date());
  
  // Create HTML content for printing
  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${title}</title>
      <style>
        @media print {
          @page {
            margin: 1cm;
            size: A4 landscape;
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 1.2;
          }
          .print-header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .print-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
          }
          .print-date {
            font-size: 10px;
            color: #666;
          }
          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 10px;
          }
          .print-table th,
          .print-table td {
            border: 1px solid #333;
            padding: 4px 6px;
            text-align: left;
            vertical-align: top;
          }
          .print-table th {
            background-color: #f5f5f5;
            font-weight: bold;
            font-size: 11px;
          }
          .print-table td {
            font-size: 10px;
          }
          .print-footer {
            margin-top: 20px;
            text-align: center;
            font-size: 10px;
            color: #666;
            border-top: 1px solid #333;
            padding-top: 10px;
          }
        }
      </style>
    </head>
    <body>
      <div class="print-header">
        <div class="print-title">${title}</div>
        <div class="print-date">Gerado em: ${currentDate}</div>
      </div>
      
      <table class="print-table">
        <thead>
          <tr>
    `;
  
  // Add headers
  columns.forEach((column: any) => {
    const headerText = column.title || column.data;
    html += `<th>${escapeHtml(headerText)}</th>`;
  });
  
  html += `
          </tr>
        </thead>
        <tbody>
    `;
  
  // Add data rows
  data.forEach((row: any, rowIndex: any) => {
    html += '<tr>';
    columns.forEach((column: any) => {
      let cellValue = row[column.data];
      
      // Format cell value based on render type
      if (column.renderType) {
        cellValue = formatCellValueForPrint(cellValue, column.renderType);
      }
      
      // Convert to string and escape HTML
      const cellText = String(cellValue || '').trim();
      html += `<td>${escapeHtml(cellText)}</td>`;
    });
    html += '</tr>';
  });
  
  html += `
        </tbody>
      </table>
      
      <div class="print-footer">
        <p>Total de registros: ${data.length}</p>
      </div>
    </body>
    </html>
  `;
  
  return html;
};

const openPrintWindow = (content: any) => {
  // Create a new window for printing
  const printWindow = window.open('', '_blank', 'width=800,height=600');
  
  if (!printWindow) {
    throw new Error('Unable to open print window. Please allow popups for this site.');
  }
  
  // Write content to the window
  printWindow.document.write(content);
  printWindow.document.close();
  
  // Wait for content to load, then trigger print
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.print();
      
      // Close the window after printing (or after user cancels)
      printWindow.onafterprint = () => {
        printWindow.close();
      };
      
      // Also close after a timeout in case onafterprint doesn't fire
      setTimeout(() => {
        if (!printWindow.closed) {
          printWindow.close();
        }
      }, 1000);
    }, 500);
  };
};

const escapeHtml = (text: any) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

const formatCellValueForPrint = (value: any, renderType: any) => {
  if (value === null || value === undefined) return '';

  switch (renderType) {
    case 'currency':
      const currencyValue = parseFloat(value) || 0;
      return n(currencyValue, 'currency');
    case 'number':
      const numberValue = parseFloat(value) || 0;
      return n(numberValue, 'decimal');
    case 'percentage':
      const percentageValue = parseFloat(value) || 0;
      return n(percentageValue / 100, 'percent');
    case 'date':
      if (!value) return '';
      const date = new Date(value);
      return formatDate(date);
    case 'datetime':
      if (!value) return '';
      const datetime = new Date(value);
      return formatDateTime(datetime);
    case 'yes_no':
      return value ? 'Sim' : 'Não';
    case 'active_inactive':
      return value ? 'Ativo' : 'Inativo';
    default:
      return String(value);
  }
};
</script>
